import {
  HASH_CASE_PARAM,
  LEGACY_HASH_CASE_PARAM,
  STORAGE_KEYS,
  dualStorageGet,
  getExternalSessionId,
  getHostBridge,
  patchHostBridge,
} from "@frontend/hostContract"
import {TRPC_PROXY_CLIENT} from "@frontend/utils/trpcClient"
import {
  isJourneyHostListenEventName,
  isWidgetHostListenEventName,
  type JourneyHostListenEventName,
  type TrackEventName,
  type WidgetHostListenEventName,
} from "@ri2/db/client"
import {activeThemeId} from "@frontend/themes/activeTheme"
import {registerHostCaseInfoApi} from "./hostCaseInfo"
import type {HostJourneyEventExtra} from "./hostJourneyEvents"

export {
  JOURNEY_HOST_LISTEN_EVENT_NAMES,
  TRACK_EVENT_NAMES,
  WIDGET_HOST_LISTEN_EVENT_NAMES,
  type JourneyHostListenEventName,
  type WidgetHostListenEventName,
  type TrackEventName,
} from "@ri2/db/client"

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void
  }
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const toValidCaseId = (value: string | null | undefined): string | null => {
  if (!value) return null

  const trimmed = value.trim()
  return UUID_REGEX.test(trimmed) ? trimmed : null
}

const getCaseIdFromHashRoute = (): string | null => {
  const routeMatch = window.location.hash.match(
    /(?:^#\/?)(?:ri2?)\/([0-9a-f-]{36})(?:\/|$|\?)/i,
  )

  return toValidCaseId(routeMatch?.[1])
}

const getCaseIdFromHashParam = (): string | null => {
  const paramMatch = window.location.hash.match(
    new RegExp(
      `(?:^#|&)(?:${HASH_CASE_PARAM}|${LEGACY_HASH_CASE_PARAM})=([^&]+)`,
    ),
  )

  if (!paramMatch) return null

  try {
    return toValidCaseId(decodeURIComponent(paramMatch[1]))
  } catch {
    return null
  }
}

const getCaseIdFromLocalStorage = (): string | null =>
  toValidCaseId(dualStorageGet(STORAGE_KEYS.caseId, STORAGE_KEYS.legacyCaseId))

const getTrackingCaseId = (caseId?: string): string | null =>
  toValidCaseId(caseId) ||
  getCaseIdFromHashRoute() ||
  getCaseIdFromHashParam() ||
  getCaseIdFromLocalStorage()

export const sendToGA = (eventName: TrackEventName): void => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({event: eventName})
}

export const sendToDbTracking = (
  eventName: TrackEventName,
  caseId?: string,
): void => {
  const trackedCaseId = getTrackingCaseId(caseId)
  if (!trackedCaseId) return

  void TRPC_PROXY_CLIENT.trackCaseEvent
    .mutate({
      caseId: trackedCaseId,
      eventName,
    })
    .catch((error: unknown) => {
      console.error("Failed to persist tracking event", eventName, error)
    })
}

export const callClarity = (...args: unknown[]): boolean => {
  if (typeof window === "undefined" || typeof window.clarity !== "function")
    return false

  try {
    window.clarity(...args)
    return true
  } catch {
    return false
  }
}

export const trackClarityEvent = (eventName: string, caseId?: string): void => {
  if (!eventName) return

  const trackedCaseId = getTrackingCaseId(caseId)
  if (trackedCaseId) callClarity("set", "caseId", trackedCaseId)

  callClarity("event", eventName)
}

type HostListener = (...args: unknown[]) => void

type ListenerRow = {listener: HostListener}

let lastUnreadCount = 0
const listenersByName = new Map<string, ListenerRow[]>()
const warnedUnknownNames = new Set<string>()

export type TrackEventExtra = {unreadCount?: number}

export type {HostJourneyEventExtra} from "./hostJourneyEvents"

export type HostEventOff = () => void

const usesJourneyHostEvents = (): boolean => activeThemeId === "bodewell"

const isPublishedHostListenEventName = (name: string): boolean =>
  usesJourneyHostEvents()
    ? isJourneyHostListenEventName(name)
    : isWidgetHostListenEventName(name)

/**
 * Existing analytics names → widget `onEvent` listen names.
 * Analytics strings stay stable; listen names are the host contract only.
 */
const WIDGET_HOST_LISTEN_BY_ANALYTICS: Partial<
  Record<TrackEventName, WidgetHostListenEventName>
> = {
  widget_chat_open: "chat_opened",
  widget_chat_open_api: "chat_opened",
  widget_chat_closed: "chat_closed",
  widget_chat_minimized: "chat_closed",
  widget_chat_minimized_api: "chat_closed",
  widget_chat_started: "chat_started",
  widget_chat_started_slideout: "chat_started",
  widget_chat_started_overlay: "chat_started",
  widget_chat_started_api: "chat_started",
  widget_chat_started_starter: "chat_started",
  widget_chat_started_typed: "chat_started",
  chat_started: "chat_started",
  chat_unread_changed: "chat_unread_changed",
}

export function onEvent(
  name: JourneyHostListenEventName,
  listener: (
    event: HostJourneyEventExtra & {
      sharedJourneyId: string | null
      entityId: string | null
    },
  ) => void,
): HostEventOff
export function onEvent(
  name: "chat_opened" | "chat_closed",
  listener: () => void,
): HostEventOff
export function onEvent(
  name: "chat_started",
  listener: (caseId: string) => void,
): HostEventOff
export function onEvent(
  name: "chat_unread_changed",
  listener: (unreadCount: number) => void,
): HostEventOff
export function onEvent(
  name: string,
  listener: (...args: any[]) => void,
): HostEventOff
export function onEvent(
  name: string,
  listener: (...args: any[]) => void,
): HostEventOff {
  if (typeof listener !== "function") {
    return (): void => undefined
  }

  if (!isPublishedHostListenEventName(name) && !warnedUnknownNames.has(name)) {
    warnedUnknownNames.add(name)
    console.warn(
      `[RepairIntelligence] onEvent("${name}") is not a published listen name; the handler will stay silent.`,
    )
  }

  const row: ListenerRow = {listener}
  const list = listenersByName.get(name) ?? []
  list.push(row)
  listenersByName.set(name, list)

  if (name === "chat_unread_changed") {
    invokeListener(row.listener, lastUnreadCount)
  }

  let removed = false
  return (): void => {
    if (removed) return
    removed = true
    const current = listenersByName.get(name)
    if (!current) return
    const index = current.indexOf(row)
    if (index >= 0) current.splice(index, 1)
  }
}

const invokeListener = (listener: HostListener, ...args: unknown[]): void => {
  try {
    listener(...args)
  } catch (error) {
    console.warn("[RepairIntelligence] onEvent listener threw", error)
  }
}

/**
 * Journey events where the host may hand off to downstream systems and
 * needs a stable correlation id even when the tenant omitted externalSessionId.
 */
const SHARED_JOURNEY_ID_CASE_FALLBACK_EVENTS = new Set<JourneyHostListenEventName>(
  [
    "cause_selected",
    "peek_selected",
    "recommendation_previewed",
    "recommendation_generated",
    "next_step_selected",
    "session_abandoned",
    "session_closed",
  ],
)

const resolveSharedJourneyId = (
  entityId: string | null,
  eventName: JourneyHostListenEventName,
): string | null => {
  const externalSessionId = getExternalSessionId()
  if (externalSessionId) return externalSessionId
  if (
    entityId &&
    SHARED_JOURNEY_ID_CASE_FALLBACK_EVENTS.has(eventName)
  ) {
    return entityId
  }
  return null
}

const buildHostJourneyPayload = (
  eventName: JourneyHostListenEventName,
  caseId?: string,
  extra?: HostJourneyEventExtra,
): HostJourneyEventExtra & {
  sharedJourneyId: string | null
  entityId: string | null
} => {
  const entityId = getTrackingCaseId(caseId)
  return {
    sharedJourneyId: resolveSharedJourneyId(entityId, eventName),
    entityId,
    ...(extra ?? {}),
  }
}

const notifyJourneyHostListeners = (
  eventName: JourneyHostListenEventName,
  caseId?: string,
  extra?: HostJourneyEventExtra,
): void => {
  const rows = listenersByName.get(eventName)
  if (!rows?.length) return

  const payload = buildHostJourneyPayload(eventName, caseId, extra)
  for (const row of [...rows]) {
    invokeListener(row.listener, payload)
  }
}

export const trackHostJourneyEvent = (
  eventName: JourneyHostListenEventName,
  caseId?: string,
  extra?: HostJourneyEventExtra,
): void => {
  if (!usesJourneyHostEvents()) return

  sendToGA(eventName)
  trackClarityEvent(eventName, caseId)
  sendToDbTracking(eventName, caseId)
  notifyJourneyHostListeners(eventName, caseId, extra)
}

const notifyWidgetHostListeners = (
  listenName: WidgetHostListenEventName,
  caseId?: string,
  unreadCount?: number,
): void => {
  const rows = listenersByName.get(listenName)
  if (!rows?.length) return

  for (const row of [...rows]) {
    if (listenName === "chat_opened" || listenName === "chat_closed") {
      invokeListener(row.listener)
      continue
    }
    if (listenName === "chat_started") {
      if (!caseId) return
      invokeListener(row.listener, caseId)
      continue
    }
    invokeListener(row.listener, unreadCount)
  }
}

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0

export const trackEvent = (
  eventName: TrackEventName,
  caseId?: string,
  extra?: TrackEventExtra,
): void => {
  if (eventName === "chat_unread_changed") {
    const nextCount = extra?.unreadCount
    if (!isNonNegativeInteger(nextCount) || nextCount === lastUnreadCount) {
      return
    }
    lastUnreadCount = nextCount
  }

  sendToGA(eventName)
  trackClarityEvent(eventName, caseId)
  sendToDbTracking(eventName, caseId)

  if (usesJourneyHostEvents()) return

  const listenName = WIDGET_HOST_LISTEN_BY_ANALYTICS[eventName]
  if (!listenName) return
  if (listenName === "chat_started" && !caseId) return

  notifyWidgetHostListeners(
    listenName,
    caseId,
    listenName === "chat_unread_changed" ? lastUnreadCount : undefined,
  )
}

type QueuedOnEvent = {
  q?: ArrayLike<unknown>[]
}

const queuedStubCalls = (stub: unknown): ArrayLike<unknown>[] => {
  if (typeof stub !== "function") return []
  const queued = (stub as QueuedOnEvent).q
  return Array.isArray(queued) ? [...queued] : []
}

/** Assigns script-added `onEvent` and drains a pre-script stub queue. */
export const registerHostEventApi = (): void => {
  const queued = queuedStubCalls(getHostBridge()?.onEvent)
  patchHostBridge({onEvent})
  registerHostCaseInfoApi()
  for (const args of queued) {
    const name = args[0]
    const listener = args[1]
    if (typeof name === "string" && typeof listener === "function") {
      onEvent(name, listener as HostListener)
    }
  }
}

/** Test-only: clears host listeners and the unread snapshot. */
export const resetHostEventStateForTests = (): void => {
  lastUnreadCount = 0
  listenersByName.clear()
  warnedUnknownNames.clear()
}
